/**
 * İçe aktarma sonucunun dökümü — hem elle `.pfai`/JSON geri yüklemede hem
 * otomatik bulut yedeğinden geri yüklemede (Y şeridi) AYNI bileşen.
 */

import type { ImportResult } from "@parafon/contracts/local-api";
import { M } from "../dil";
import { formatCount } from "../lib/format";

const IMPORT_KINDS = [
  ["accounts", M.iceAktar.rapor.tur.accounts],
  ["transactions", M.iceAktar.rapor.tur.transactions],
  ["manualValuations", M.iceAktar.rapor.tur.manualValuations],
  ["manualPrices", M.iceAktar.rapor.tur.manualPrices],
  ["settings", M.iceAktar.rapor.tur.settings],
] as const;

/**
 * İÇE AKTARMA RAPORU — canlı kusurdan doğdu.
 *
 * Eski hâli yalnızca "yazılan" sayısını gösteriyordu. Kullanıcı kendi
 * cihazından aldığı ilk yedeği geri yükledi, ekranda "Hesap: 0 / Hareket: 0"
 * gördü ve haklı olarak verisini kaybettiğini düşündü. Oysa dosyadaki
 * kayıtlar deftere birebir eşitti; yazılacak bir şey yoktu.
 *
 * Bu yüzden burada YAZILAN sayısı tek başına gösterilmiyor: dosyada ne vardı,
 * kaçı yazıldı, kaçı zaten aynıydı, kaçı atlandı — dördü birlikte. Üstte de
 * bunların insan diliyle özeti var, çünkü "0" gören kullanıcı tabloyu
 * okumadan önce paniğe kapılıyor.
 */
export function ImportReport({ result, title }: { readonly result: ImportResult; readonly title: string }) {
  const rows = IMPORT_KINDS.map(([key, label]) => ({ key, label, r: result[key] })).filter(
    (x) => x.r.found > 0,
  );
  const toplam = IMPORT_KINDS.reduce(
    (acc, [key]) => {
      const r = result[key];
      return {
        found: acc.found + r.found,
        written: acc.written + r.written,
        unchanged: acc.unchanged + r.unchanged,
      };
    },
    { found: 0, written: 0, unchanged: 0 },
  );

  const ozet =
    toplam.found === 0
      ? M.iceAktar.rapor.bos
      : toplam.written === 0 && toplam.unchanged === toplam.found
        ? M.iceAktar.rapor.hepsiAyni
        : toplam.written === 0
          ? M.iceAktar.rapor.hicYazilmadi
          : null;

  return (
    <div className="mt-3 rounded-pf border border-line bg-surface-2 p-3 text-[13px]">
      <p className="font-semibold text-ink">{title}</p>
      {ozet !== null && <p className="mt-1 text-muted">{ozet}</p>}
      {rows.length > 0 && (
        <table className="mt-2 w-full text-[12px]">
          <thead>
            <tr className="text-left text-faint">
              <th className="font-normal">&nbsp;</th>
              <th className="text-right font-normal">{M.iceAktar.rapor.dosyada}</th>
              <th className="text-right font-normal">{M.iceAktar.rapor.yazildi}</th>
              <th className="text-right font-normal">{M.iceAktar.rapor.zatenAyni}</th>
              <th className="text-right font-normal">{M.iceAktar.rapor.atlandi}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ key, label, r }) => (
              <tr key={key} className="border-t border-line">
                <td className="py-1 text-muted">{label}</td>
                <td className="pf-num py-1 text-right text-ink">{formatCount(r.found)}</td>
                <td className="pf-num py-1 text-right text-ink">{formatCount(r.written)}</td>
                <td className="pf-num py-1 text-right text-muted">{formatCount(r.unchanged)}</td>
                <td
                  className={`pf-num py-1 text-right ${r.skipped > 0 ? "text-warn" : "text-muted"}`}
                >
                  {formatCount(r.skipped)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {result.skipped.length > 0 ? (
        <details className="mt-2">
          <summary className="cursor-pointer text-[12px] text-warn">
            {M.iceAktar.rapor.atlananlar(formatCount(result.skipped.length))}
          </summary>
          <ul className="mt-1 space-y-0.5 text-[12px] text-muted">
            {result.skipped.map((s, i) => (
              <li key={`${s.kind}-${s.id}-${i}`}>
                <span className="pf-tag">{s.kind}</span> <span className="pf-num">{s.id}</span> —{" "}
                {s.reason}
              </li>
            ))}
          </ul>
        </details>
      ) : (
        <p className="mt-2 text-[12px] text-muted">{M.iceAktar.rapor.hicAtlanmadi}</p>
      )}
    </div>
  );
}
